import { useEffect, useState } from "react";
import { PublicTransportImportType } from "../../../../constant/PublicTransportImportType";
import { useParams } from "react-router-dom";
import { usePublicTransportService } from "../../../../hooks";
import SortableTable from "../../../Table/SortableTable";
import { TableColumn } from "../../../../types/Table/TableColumns";
import { Button, Modal } from "react-bootstrap";
import { PublicTransportImport } from "../../../../types/LayerAdmin/PublicTransportImport";
import ThemedModal from "../../../../@ui/Modal/ThemedModal";
import { set } from "lodash";

const PtImportsView = () => {
  const publicTransportService = usePublicTransportService();

  const [loading, setLoading] = useState<boolean>(false);
  const [imports, setImports] = useState<PublicTransportImport[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [importContext, setImportContext] = useState<PublicTransportImport | null>(null);


  const { quarter } = useParams();

  useEffect(() => {
    if (quarter === undefined) return;
    setLoading(true);
    publicTransportService
      .GetImports(quarter)
      .then((response: any) => {
        setImports(response);
      })
      .catch(() => { })
      .finally(() => {
        setLoading(false);
      });
  }, [quarter]);

  const getImportLabel = (importType: PublicTransportImportType) => {
    switch (importType) {
      case PublicTransportImportType.AtocCif:
        return "ATOC";
      case PublicTransportImportType.Traveline:
        return "Traveline";
      case PublicTransportImportType.TransitLand:
        return "TransitLand";
      case PublicTransportImportType.Gtfs:
        return "One Off Gtfs";
      case PublicTransportImportType.NCSD:
        return "NCSD";
      case PublicTransportImportType.Translink:
        return "Translink";
    }
  };

  const cols = [
    {
      field: "importQuarter",
      displayName: "Name",
      sortable: true,
    },
    {
      field: "publicTransportImportType",
      displayName: "Type",
      sortable: true,
      renderContents: (importType: PublicTransportImportType) => getImportLabel(importType)
    },
    {
      field: "importDate",
      displayName: "Date",
      sortable: true,
      renderContents: (date: string) => new Date(date).toLocaleString()
    },
    {
      field:"routesImported",
      displayName:"Routes Imported",
      sortable:true
    },
    {
      field: "published",
      displayName: "Published",
      sortable: true,
      renderContents: (published: boolean) => published ? "Yes" : "No"
    },
    {
      field: "published",
      displayName: "Actions",
      sortable: false,
      renderContents: (published: boolean, row: any) => {
        return (
          <>
            {published && <Button onClick={() => { showConfirmationModal(row); }} variant="danger">UnPublish</Button>}
            {!published && <Button onClick={() => { showConfirmationModal(row); }} variant="success">Publish</Button>}
          </>
        )
      }
    }
  ] as TableColumn[];

  const showConfirmationModal = (ptImport: PublicTransportImport) => {
    setImportContext(ptImport);
    setShowConfirmation(true);
  }

  const publish = (ptImport: any) => {
    publicTransportService.PublishImport(ptImport.publicTransportImportId).then(() => {
      updatePublishStatus(ptImport, true);
    }).catch(() => {

    }).finally(() => {
      closeConfirmationModal();
    });
  };

  const unpublish = (ptImport: PublicTransportImport) => {
    publicTransportService.UnpublishImport(ptImport.publicTransportImportId).then(() => {
      updatePublishStatus(ptImport, false);
    }).catch(() => {

    }).finally(() => {
      closeConfirmationModal();
    });
  };

  const updatePublishStatus = (ptImport: PublicTransportImport, published: boolean) => {
    const updatedImports = imports.map(imp => ({ ...imp }));

    const importIndex = updatedImports.findIndex(imp => imp.publicTransportImportId === ptImport.publicTransportImportId);

    if (importIndex !== -1) {
      updatedImports[importIndex].published = published;
      setImports(updatedImports);
    }
  };

  const onActionConfirmed = () => {
    if (!importContext) return;

    if (importContext.published) {
      unpublish(importContext);
    } else {
      publish(importContext);
    }
  }

  const closeConfirmationModal = () => {
    setShowConfirmation(false);
    setImportContext(null);
  }

  return (
    <>
      {" "}
      <SortableTable
        loading={loading}
        columns={cols}
        data={imports}
        noDataMessage="No Successful Imports Found"
      />
      <ThemedModal centered show={showConfirmation} onHide={closeConfirmationModal}>
        {importContext && <>
          <Modal.Header>
            <Modal.Title>{importContext.published ? "UnPublish" : "Publish"} {getImportLabel(importContext.publicTransportImportType)} Import?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {importContext.published && <p>This will stop customers from being able to request it's data.</p>}
            {!importContext.published && <p>This will allow customers to request it's data.</p>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeConfirmationModal}>Cancel</Button>
            <Button variant={importContext.published ? "danger" : "success"} onClick={onActionConfirmed} >{importContext.published ? "UnPublish" : "Publish"} </Button>
          </Modal.Footer></>}
      </ThemedModal>
    </>
  );
};

export default PtImportsView;
